/**
 * The Palindrome Checker: does a word, number or sentence read the same backwards?
 *
 * Text is first cut into comparison units — letters (a base character with its combining
 * marks, so an accented é stays one unit) or whole words — after the reader's choices are
 * applied: fold case, drop accents, and drop spaces and punctuation. A text is a
 * palindrome when its unit list equals its reverse. Each unit remembers where it sat in
 * the original text, which is how the longest palindromic stretch found inside a longer
 * text can be shown with its punctuation restored.
 */

export interface PalindromeOptions {
  ignoreCase: boolean;
  /** Keep only letters and digits. */
  ignorePunctuation: boolean;
  /** Treat é as e. */
  ignoreAccents: boolean;
}

export interface Unit {
  text: string;
  /** Offsets of the unit in the original string. */
  start: number;
  end: number;
}

const RE_CLUSTER = /\P{M}\p{M}*/gu;
const RE_MARKS = /\p{M}/gu;
const RE_NOT_ALNUM = /[^\p{L}\p{N}]/gu;
const RE_WORD = /[\p{L}\p{N}]+(?:['’][\p{L}\p{N}]+)*/gu;
const RE_SENTENCE_BREAK = /(?<=[.!?…])\s+|\n+/;

function fold(text: string, options: PalindromeOptions): string {
  let result = text;
  if (options.ignoreAccents) {
    result = result.normalize("NFD").replace(RE_MARKS, "").normalize("NFC");
  }
  if (options.ignoreCase) {
    result = result.toLowerCase();
  }
  if (options.ignorePunctuation) {
    result = result.replace(RE_NOT_ALNUM, "");
  }
  return result;
}

/** The letters of a text as comparison units, with their positions in the original. */
export function letterUnits(text: string, options: PalindromeOptions): Unit[] {
  const units: Unit[] = [];
  for (const match of text.matchAll(RE_CLUSTER)) {
    const folded = fold(match[0], options);
    if (folded) {
      units.push({
        text: folded,
        start: match.index,
        end: match.index + match[0].length,
      });
    }
  }
  return units;
}

/** The words of a text as comparison units (apostrophes inside a word are dropped). */
export function wordUnits(text: string, options: PalindromeOptions): Unit[] {
  const units: Unit[] = [];
  for (const match of text.matchAll(RE_WORD)) {
    const folded = fold(match[0], { ...options, ignorePunctuation: true });
    if (folded) {
      units.push({
        text: folded,
        start: match.index,
        end: match.index + match[0].length,
      });
    }
  }
  return units;
}

export type Compare = "letters" | "words";

export interface Mismatch {
  /** Zero-based position from the start. */
  index: number;
  left: string;
  right: string;
}

export interface CheckResult {
  units: string[];
  palindrome: boolean;
  mismatch: Mismatch | null;
}

/** Is the text a palindrome, and if not, where do the two ends first disagree? */
export function checkPalindrome(
  text: string,
  compare: Compare,
  options: PalindromeOptions
): CheckResult {
  const units = (
    compare === "words" ? wordUnits(text, options) : letterUnits(text, options)
  ).map((unit) => unit.text);
  const last = units.length - 1;
  for (let i = 0; i < units.length / 2; i += 1) {
    if (units[i] !== units[last - i]) {
      return {
        units,
        palindrome: false,
        mismatch: { index: i, left: units[i], right: units[last - i] },
      };
    }
  }
  return { units, palindrome: units.length > 0, mismatch: null };
}

export interface Stretch {
  /** The stretch as written, punctuation and spacing included. */
  text: string;
  /** Number of units (letters) in it. */
  length: number;
}

/**
 * The longest palindromic run of units (Manacher's algorithm, linear time), or null when
 * nothing longer than a single unit qualifies.
 */
export function longestStretch(
  text: string,
  options: PalindromeOptions
): Stretch | null {
  const units = letterUnits(text, { ...options, ignorePunctuation: true });
  if (units.length < 2) {
    return null;
  }
  // Interleave separators so odd and even palindromes share one loop.
  const padded: string[] = ["^"];
  for (const unit of units) {
    padded.push("#", unit.text);
  }
  padded.push("#", "$");
  const radius = new Array<number>(padded.length).fill(0);
  let center = 0;
  let right = 0;
  let best = 0;
  let bestCenter = 0;
  for (let i = 1; i < padded.length - 1; i += 1) {
    if (i < right) {
      radius[i] = Math.min(right - i, radius[2 * center - i]);
    }
    while (padded[i + 1 + radius[i]] === padded[i - 1 - radius[i]]) {
      radius[i] += 1;
    }
    if (i + radius[i] > right) {
      center = i;
      right = i + radius[i];
    }
    if (radius[i] > best) {
      best = radius[i];
      bestCenter = i;
    }
  }
  if (best < 2) {
    return null;
  }
  const first = Math.floor((bestCenter - best) / 2);
  const lastUnit = first + best - 1;
  return {
    text: text.slice(units[first].start, units[lastUnit].end),
    length: best,
  };
}

export interface FoundWord {
  word: string;
  count: number;
}

export interface ScanResult {
  words: FoundWord[];
  sentences: string[];
  longest: Stretch | null;
}

/** Palindromic words, palindromic sentences and the longest stretch inside a text. */
export function scanForPalindromes(
  text: string,
  options: PalindromeOptions,
  minLength: number
): ScanResult {
  const counts = new Map<string, FoundWord>();
  for (const match of text.matchAll(RE_WORD)) {
    const checked = checkPalindrome(match[0], "letters", {
      ...options,
      ignorePunctuation: true,
    });
    if (checked.palindrome && checked.units.length >= minLength) {
      const key = checked.units.join("");
      const entry = counts.get(key);
      if (entry) {
        entry.count += 1;
      } else {
        counts.set(key, { word: key, count: 1 });
      }
    }
  }
  const words = [...counts.values()].sort(
    (a, b) => b.word.length - a.word.length || a.word.localeCompare(b.word)
  );
  const sentences: string[] = [];
  for (const piece of text.split(RE_SENTENCE_BREAK)) {
    const sentence = piece.trim();
    const checked = checkPalindrome(sentence, "letters", {
      ...options,
      ignorePunctuation: true,
    });
    if (checked.palindrome && checked.units.length >= minLength) {
      sentences.push(sentence);
    }
  }
  return { words, sentences, longest: longestStretch(text, options) };
}
